/**
 * shared/attachmentsPanel/FileList.jsx - a folder's file cards (label,
 * link, move, preview, download, delete). A render function called inline, so
 * it renders the same DOM the panel used to build itself.
 */
import { normalizeDocRef, REF_OPTIONAL, previewAttachment, downloadAttachment } from '../../../utils/attachmentPreview';
import { DOC_FOLDERS } from '../../../data/docFolders';
import { DocTag, LinkBadge, humanSize, formatWhen, FIELD, LINK_BTN, GREEN } from './panelParts';

export function renderFileList({
  loading,
  visible,
  view,
  canLink,
  opens,
  linkName,
  linkedRfq,
  linkNumber,
  linkType,
  editing,
  setEditing,
  saveLabel,
  moving,
  setMoving,
  subFolders,
  saveMove,
  base,
  linking,
  setLink,
  canDelete,
  remove,
}) {
  return (
    <>
      {/* File list */}
      {loading ? (
        <div style={{ fontSize: 11, color: '#aaa', padding: '12px 2px' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 11, color: '#bbb', padding: '12px 2px' }}>Nothing in {view.label} yet — pick a file above to add one.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((f) => (
            <div
              key={f.id}
              style={{
                position: 'relative',
                background: '#fff',
                border: '1px solid #E6EBE1',
                borderRadius: 8,
                padding: '10px 12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1a1a1a', wordBreak: 'break-word' }}>{f.filename}</span>
                <DocTag type={f.doc_type || 'general'} refNo={f.doc_ref} />
                {canLink && !f._staged && opens && opens.file.id === f.id && (
                  <LinkBadge chosen={opens.how === 'chosen'}>
                    {opens.how === 'chosen' ? `Opens from ${linkName} · you chose this` : `Opens from ${linkName} · automatic (newest PDF)`}
                  </LinkBadge>
                )}
                {canLink && !f._staged && linkedRfq && linkedRfq.id === f.id && (
                  <LinkBadge chosen>{linkNumber ? `RFQ for Quote ${linkNumber}` : 'This record\u2019s RFQ'}</LinkBadge>
                )}
              </div>
              {/* A file labelled with a number that is not this record's: it will
                  never open from the table here, which is easy to miss. */}
              {canLink &&
                linkType !== 'rfq' &&
                linkNumber &&
                f.doc_type === linkType &&
                f.doc_ref &&
                normalizeDocRef(f.doc_ref) !== normalizeDocRef(linkNumber) && (
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>
                    Labelled {linkType === 'po' ? 'PO' : 'Quote'} {f.doc_ref}, which is not this record&apos;s{' '}
                    {linkType === 'po' ? 'PO' : 'quote'} number ({linkNumber}).
                  </div>
                )}
              <div style={{ fontSize: 9.5, color: '#8a8a8a', marginBottom: 7 }}>
                {humanSize(f.size)} <span style={{ color: '#ccc' }}>·</span> {f.uploaded_by || 'Unknown'}{' '}
                <span style={{ color: '#ccc' }}>·</span> {formatWhen(f.created_at)}
              </div>
              {editing?.id === f.id ? (
                <div style={{ background: '#F6F9F3', border: '1px solid #DCE7D6', borderRadius: 6, padding: '8px 10px', marginBottom: 2 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <select
                      value={editing.doc_type}
                      onChange={(e) => setEditing((ed) => ({ ...ed, doc_type: e.target.value, error: '' }))}
                      style={{ ...FIELD, flex: '1 1 100px', fontWeight: 700 }}
                    >
                      <option value="invoice">Invoice</option>
                      <option value="po">PO</option>
                      <option value="quote">Quote</option>
                      <option value="rfq">RFQ</option>
                      <option value="general">General / other</option>
                    </select>
                    <input
                      type="text"
                      value={editing.doc_ref || ''}
                      onChange={(e) => setEditing((ed) => ({ ...ed, doc_ref: e.target.value, error: '' }))}
                      placeholder={REF_OPTIONAL.has(editing.doc_type) ? 'Number (optional)' : 'Number *'}
                      style={{ ...FIELD, flex: '1 1 110px' }}
                    />
                  </div>
                  {editing.error && <div style={{ marginTop: 6, fontSize: 10, fontWeight: 700, color: '#B71C1C' }}>{editing.error}</div>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                    <button type="button" style={{ ...LINK_BTN, color: '#B71C1C' }} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                    <button type="button" style={LINK_BTN} onClick={saveLabel}>
                      Save label
                    </button>
                  </div>
                </div>
              ) : moving?.id === f.id ? (
                /* Refile: pick a folder, then optionally one of its sub-folders. */
                <div style={{ background: '#F6F9F3', border: '1px solid #DCE7D6', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <select
                      value={moving.folder || ''}
                      onChange={(e) => setMoving((m) => ({ ...m, folder: e.target.value, subfolder_id: '' }))}
                      style={{ ...FIELD, flex: '1 1 140px', fontWeight: 700 }}
                    >
                      <option value="">Unfiled</option>
                      {DOC_FOLDERS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    {moving.folder && subFolders.some((s) => s.folder_key === moving.folder) && (
                      <select
                        value={moving.subfolder_id || ''}
                        onChange={(e) => setMoving((m) => ({ ...m, subfolder_id: e.target.value }))}
                        style={{ ...FIELD, flex: '1 1 120px' }}
                      >
                        <option value="">(main folder)</option>
                        {subFolders
                          .filter((s) => s.folder_key === moving.folder)
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                    <button type="button" style={{ ...LINK_BTN, color: '#B71C1C' }} onClick={() => setMoving(null)}>
                      Cancel
                    </button>
                    <button type="button" style={LINK_BTN} onClick={saveMove}>
                      Move
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* A staged file exists only in this browser - there is
                      nothing on the server to preview, download or re-file
                      yet. It uploads when the opportunity is saved. */}
                  {f._staged ? (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#B07503' }}>uploads when you save</span>
                  ) : (
                    <>
                      <button type="button" style={LINK_BTN} onClick={() => previewAttachment(f.id, base)}>
                        Preview
                      </button>
                      <button
                        type="button"
                        style={{ ...LINK_BTN, color: '#1565C0' }}
                        onClick={() => downloadAttachment(f.id, f.filename, base)}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        style={{ ...LINK_BTN, color: '#E65100' }}
                        onClick={() => setEditing({ id: f.id, doc_type: f.doc_type || 'general', doc_ref: f.doc_ref || '', error: '' })}
                      >
                        Edit label
                      </button>
                      <button
                        type="button"
                        style={{ ...LINK_BTN, color: '#6A1B9A' }}
                        onClick={() => setMoving({ id: f.id, folder: f.folder || '', subfolder_id: f.subfolder_id || '' })}
                      >
                        Move
                      </button>
                      {canLink &&
                        linkType !== 'rfq' &&
                        linkNumber &&
                        (opens && opens.file.id === f.id && opens.how === 'chosen' ? (
                          <button
                            type="button"
                            disabled={linking === f.id}
                            style={{ ...LINK_BTN, color: '#5A6557' }}
                            title="Let the app pick again (newest PDF)"
                            onClick={() => setLink(f, { linked: false })}
                          >
                            {linking === f.id ? 'Saving...' : 'Unlink'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={linking === f.id}
                            style={{ ...LINK_BTN, color: GREEN }}
                            title={`Make this the file the ${linkName} opens from the tables`}
                            onClick={() => setLink(f, { doc_type: linkType, doc_ref: linkNumber, linked: true })}
                          >
                            {linking === f.id ? 'Saving...' : `Link to ${linkName}`}
                          </button>
                        ))}
                      {canLink &&
                        linkType === 'rfq' &&
                        (linkedRfq && linkedRfq.id === f.id ? (
                          <button
                            type="button"
                            disabled={linking === f.id}
                            style={{ ...LINK_BTN, color: '#5A6557' }}
                            onClick={() => setLink(f, { linked: false })}
                          >
                            {linking === f.id ? 'Saving...' : 'Unlink'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={linking === f.id}
                            style={{ ...LINK_BTN, color: GREEN }}
                            title={linkNumber ? `Mark this as the RFQ for Quote ${linkNumber}` : 'Mark this as the record\u2019s RFQ'}
                            onClick={() =>
                              setLink(f, { doc_type: 'rfq', doc_ref: f.doc_type === 'rfq' ? f.doc_ref || '' : '', linked: true })
                            }
                          >
                            {linking === f.id
                              ? 'Saving...'
                              : linkNumber
                                ? `Link as RFQ for Quote ${linkNumber}`
                                : 'Link as this record\u2019s RFQ'}
                          </button>
                        ))}
                    </>
                  )}
                  {/* Was an 18px pale × in the card's top corner, which read as
                      "there is no delete button". Same action, same permission,
                      somewhere a person can actually find it. */}
                  {(canDelete || f._staged) && (
                    <button
                      type="button"
                      aria-label="Delete attachment"
                      title={f._staged ? 'Remove from the upload queue' : 'Delete this file — permanent, cannot be undone'}
                      style={{ ...LINK_BTN, color: '#B71C1C' }}
                      onClick={() => remove(f.id, f.filename, f._staged ? f._index : null)}
                    >
                      {f._staged ? 'Remove' : 'Delete'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
